import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { isProd } from "src/helpers/env.helpers";
import useIsTabActive from "src/hooks/useIsTabActive";
import { useGetSurveyQuery, useGetSurveyStatsQuery, useGetSurveySummaryQuery } from "src/redux/survey/survey.endpoints";
import { SurveyFull, SurveyStats, SurveyStatus, SurveySummaryData } from "src/redux/survey/survey.types";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "src/redux/redux-store";
import { clearSelectedAnswers } from "src/redux/survey/survey-selected-answers.slice";

const { Complete, Live } = SurveyStatus;
const SUMMARY_REFRESH_INTERVAL = 30000;

interface UseViewSurveyDataReturn {
  readonly isShare: boolean,
  // todo we aren't returning this yet everything seems to work properly?
  readonly shareId?: string,
  readonly showSummaries: boolean,
  readonly stats?: SurveyStats,
  readonly survey?: SurveyFull,
  readonly surveyId: string;
  readonly surveyError?: boolean,
  readonly summary?: SurveySummaryData,
}

export const useViewSurveyData = (): UseViewSurveyDataReturn => {
  const isTabActive = useIsTabActive();
  const dispatch = useDispatch();
  const selectedAnswerIds = useSelector((state: RootState) => {
    return state.selectedAnswers.selectedAnswerIds;
  });
  // const refreshConfig = FirebaseService.getRemoteConfigNumber('RESULTS_AUTO_REFRESH_SECONDS') || 0;
  const refreshConfig = 0;

  const { extSurveyId } = useParams<{ extSurveyId?: string }>();

  const [searchParams] = useSearchParams();
  // const extSurveyId = searchParams.get('ext-survey-id') ?? '';
  const shareId = searchParams.get('share-id') ?? '';
  const surveyId = extSurveyId !== '' ? extSurveyId : shareId;
  const isShare = Boolean(shareId);

  // Clear selected answers when survey changes
  useEffect(() => {
    if (surveyId) {
      dispatch(clearSelectedAnswers());
    }
  }, [surveyId, dispatch]);

  const fetchParams = { ext_survey_id: surveyId || '', share: isShare, selected_survey_answers: selectedAnswerIds };
  const [statsRefreshInterval, setStatsRefreshInterval] = useState<number | undefined>(undefined);
  const surveyFetchOptions = { skip: !surveyId, refetchOnMountOrArgChange: true, pollingInterval: statsRefreshInterval };
  const { data: survey, isError: surveyError } = useGetSurveyQuery(fetchParams, surveyFetchOptions);
  const { data: stats } = useGetSurveyStatsQuery(fetchParams, surveyFetchOptions);

  const hasFetchedAfterComplete = useRef(false);
  const [summaryRefreshInterval, setSummaryRefreshInterval] = useState<number | undefined>(undefined);
  const showSummaries = survey?.surveyStatus === SurveyStatus.Complete || survey?.surveyStatus === SurveyStatus.Live;
  const { data: summary } = useGetSurveySummaryQuery(fetchParams, { pollingInterval: summaryRefreshInterval, skip: !showSummaries || !summaryRefreshInterval });

  useEffect(() => {
    // On QA or Perf, most surveys are not answered by real users
    // so we should stop refreshing after AI finishes
    let blockedByAICompletion = false;
    if (!isProd() && survey && survey.maxAICompletes !== null && survey.maxAICompletes > 0 && survey.maxCompletes !== 0 && stats) {
      blockedByAICompletion = stats.aiUsers >= survey.maxAICompletes;
    }

    const shouldRefreshAsComplete = survey?.surveyStatus === Complete && !hasFetchedAfterComplete.current;
    const shouldRefresh = Boolean(refreshConfig && (survey?.surveyStatus === SurveyStatus.Live || shouldRefreshAsComplete) && !blockedByAICompletion && isTabActive);
    if (shouldRefresh && shouldRefreshAsComplete) {
      hasFetchedAfterComplete.current = true;
    }
    setStatsRefreshInterval(shouldRefresh ? refreshConfig * 1000 : undefined);
  }, [refreshConfig, survey, stats, isTabActive]);

  useEffect(() => {
    showSummaries && setSummaryRefreshInterval(summary ? undefined : SUMMARY_REFRESH_INTERVAL);
  }, [summary, showSummaries]);

  return {
    isShare,
    showSummaries,
    stats,
    survey,
    surveyError,
    summary,
    surveyId: surveyId || '',
  };
};

